import React, { useMemo, useState } from "react";

import { DeploymentService } from "@packages/client";
import { useIntl } from "react-intl";

import { capitalize } from "Libs/utils";
import TimeframeControl from "src/organization/pages/project/environment/metrics/components/timeframeControl/TimeframeControl";
import LargeMetric from "src/organization/pages/project/environment/metrics/containers/Service/LargeMetric";
import { serviceOrder } from "src/organization/pages/project/environment/metrics/containers/Service/util";
import useActivityToggle from "src/organization/pages/project/environment/metrics/hooks/useActivityToggle";
import useInitialTimeframe from "src/organization/pages/project/environment/metrics/hooks/useInitialTimeframe";
import useLoadAutoScale from "src/organization/pages/project/environment/metrics/hooks/useLoadAutoScale";
import { MetricsContext } from "src/organization/pages/project/environment/metrics/hooks/useMetricsContext";
import usePinnedTabsSync from "src/organization/pages/project/environment/metrics/hooks/usePinnedTabsSync";
import useStickyTimecontrol from "src/organization/pages/project/environment/metrics/hooks/useStickyTimecontrol";
import InstanceSelector from "src/organization/pages/project/environment/metrics/resources/grid/InstanceSelector";
import useGridPoller from "src/organization/pages/project/environment/metrics/resources/grid/useGridPoller";
import { useStubService } from "src/organization/pages/project/environment/metrics/resources/grid/util";
import { getPollInterval } from "src/organization/pages/project/environment/metrics/utils/polling/utils";

import MetricsHeader from "./MetricsHeader";
import * as S from "./ServiceMetrics.styles";
import { MainTitle, SticlyHeader } from "./ServiceMetrics.styles";

type Props = {
  serviceName: string;
};

const ServiceUnits = ({ serviceName = "" }: Props) => {
  useLoadAutoScale();
  const intl = useIntl();

  const [instanceHost, setInstanceHost] = useState<string[]>();
  const [timeFrame, setTimeFrame] = useState(useInitialTimeframe());
  const [isDeployment, setDeployment] = useActivityToggle();

  const { all } = useStubService();

  const service = useMemo(
    () => all?.find(({ name }) => name === serviceName),
    [all, serviceName]
  );

  const { data, status, lastUpdated } = useGridPoller({
    serviceName,
    timeFrame
  });
  const { metrics, instances = [] } = data || {};

  const { onSyncChange, isSynced, canJoinSync } = usePinnedTabsSync({
    setTimeFrame,
    timeFrame
  });

  const deployments = useMemo(
    () => ({ enabled: isDeployment, onChange: setDeployment }),
    [isDeployment, setDeployment]
  );

  const handleSyncChange = (isSynced: boolean) => {
    onSyncChange(isSynced, timeFrame);
  };

  const orderedMetrics = serviceOrder(metrics);
  const selectedHost = instanceHost || instances || [];
  const serviceKind = capitalize(service?.["kind"] || service?.["class"]);

  const interval = getPollInterval(timeFrame);
  const stickyRef = useStickyTimecontrol();

  return (
    <MetricsContext.Provider
      value={{
        timeFrame,
        setTimeFrame,
        isDeployment
      }}
    >
      <SticlyHeader ref={stickyRef}>
        <MainTitle>{intl.formatMessage({ id: "metrics.title" })}</MainTitle>
        <TimeframeControl
          timeframe={{
            onChange: setTimeFrame,
            timeframe: timeFrame
          }}
          isUpdating={status.isLoading}
          pollInterval={interval}
          lastUpdated={lastUpdated}
          deployment={deployments}
          tabSync={{
            onSyncChange: handleSyncChange,
            isSynced,
            canJoinSync
          }}
        >
          {instances.length > 1 && (
            <InstanceSelector
              instances={instances}
              selected={selectedHost}
              onChange={setInstanceHost}
            />
          )}
        </TimeframeControl>
      </SticlyHeader>
      <S.ChartLayout>
        <MetricsHeader service={service} />
        <S.GridLG>
          {orderedMetrics.map(([unit, value], index) => {
            return (
              <LargeMetric
                service={service as DeploymentService}
                key={`${unit}-${index}`}
                isLoading={status.refresh}
                onBrush={setTimeFrame}
                hosts={selectedHost}
                unit={unit}
                value={value}
                serviceKind={serviceKind}
              />
            );
          })}
        </S.GridLG>
      </S.ChartLayout>
    </MetricsContext.Provider>
  );
};
export default ServiceUnits;
